/**
 * React Query hooks for Deutschlandatlas API
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import * as deutschlandatlasApi from '../services/deutschlandatlasApi'
import type {
  DaExecuteRequest,
  DaExecuteResponse,
  DaFilesResponse,
  DaImportStatus,
  DaScanResponse,
  DaUploadResponse,
} from '../services/deutschlandatlasApi'

// ============================================================================
// Files
// ============================================================================

export function useDaFiles() {
  return useQuery<DaFilesResponse>({
    queryKey: ['deutschlandatlas', 'files'],
    queryFn: deutschlandatlasApi.listFiles,
    staleTime: 30 * 1000, // 30 seconds
  })
}

export function useDaUpload() {
  const queryClient = useQueryClient()

  return useMutation<DaUploadResponse, Error, File>({
    mutationFn: deutschlandatlasApi.uploadXlsx,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deutschlandatlas', 'files'] })
    },
  })
}

// ============================================================================
// Scan
// ============================================================================

export function useDaScan(file: string | null) {
  return useQuery<DaScanResponse>({
    queryKey: ['deutschlandatlas', 'scan', file],
    queryFn: () => deutschlandatlasApi.scanFile(file as string),
    enabled: file !== null,
    staleTime: 60 * 1000, // 1 minute
  })
}

// ============================================================================
// Import Execution
// ============================================================================

export function useDaExecuteImport() {
  const queryClient = useQueryClient()

  return useMutation<DaExecuteResponse, Error, DaExecuteRequest>({
    mutationFn: deutschlandatlasApi.executeImport,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] })
    },
  })
}

export function useDaImportStatus(jobId: string | null) {
  return useQuery<DaImportStatus>({
    queryKey: ['deutschlandatlas', 'import-status', jobId],
    queryFn: () => deutschlandatlasApi.getStatus(jobId as string),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status === 'completed' || status === 'failed' ? false : 1000
    },
  })
}
